import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FlaskConical, Loader2, RefreshCw, RotateCcw, Power, Copy, Check, ShieldAlert } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";

export const DemoEnvironmentCard = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState(null); // {email, password} — shown once after generation
  const [copied, setCopied] = useState("");
  const [confirmReset, setConfirmReset] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/demo`, { headers: authHeaders() })
      .then((r) => setStatus(r.data))
      .catch((err) => setStatus(err.response?.status === 403 ? { hidden: true } : { error: true }));
  }, []);
  useEffect(load, [load]);

  const act = async (path, body = {}) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/demo/${path}`, body, { headers: authHeaders() });
      return r.data;
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Demo action failed.");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    const d = await act("credentials");
    if (d) {
      setCreds({ email: d.email, password: d.password });
      toast.success(d.reseeded ? "Demo credentials created and the demo world was seeded." : "Demo credentials regenerated — the old password stopped working.");
      load();
    }
  };

  const toggle = async () => {
    const d = await act("toggle", { enabled: !status.enabled });
    if (d) {
      toast.success(d.enabled ? "Demo access enabled." : "Demo access disabled — active demo sessions are blocked immediately.");
      load();
    }
  };

  const reset = async () => {
    const d = await act("reset");
    if (d) {
      setConfirmReset(false);
      toast.success("Demo world reset to the synthetic baseline.");
      load();
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  if (!status || status.hidden) return null;

  return (
    <div data-testid="demo-environment-card" className="mt-6 rounded-3xl border border-amber-500/25 bg-[var(--pv-card)] p-6 sm:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <FlaskConical className="h-5 w-5 text-amber-400" />
          <h3 className="font-display text-base font-bold text-[var(--pv-text)]">Demo Environment</h3>
          {status?.enabled && <span data-testid="demo-enabled-badge" className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-400">Enabled</span>}
          {status && !status.enabled && <span data-testid="demo-disabled-badge" className="rounded-full bg-slate-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-400">Disabled</span>}
        </div>
        <p data-testid="demo-last-login" className="text-[11px] text-[var(--pv-text-4)]">
          {status?.last_login ? `Last demo login: ${new Date(status.last_login).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : "Never logged in"}
        </p>
      </div>
      <p className="mt-2 max-w-2xl text-xs text-[var(--pv-text-4)]">
        A standalone login that opens a fully synthetic copy of Melto Studios — fake crew, fake jobs, fake money. Demo visitors explore every feature in an isolated database: they can never read, change, or trigger anything real, and nothing they do touches your production data, analytics, or integrations.
      </p>

      {creds && (
        <div data-testid="demo-credentials-reveal" className="mt-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4">
          <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-300"><ShieldAlert className="h-3.5 w-3.5" /> Shown once — copy these now</p>
          <div className="mt-2.5 space-y-2">
            {[["Email", creds.email, "email"], ["Password", creds.password, "password"]].map(([label, value, key]) => (
              <div key={key} className="flex items-center gap-2">
                <span className="w-16 text-[10px] font-bold uppercase text-[var(--pv-text-4)]">{label}</span>
                <code data-testid={`demo-cred-${key}`} className="flex-1 rounded-lg border border-[var(--pv-border)] bg-[var(--pv-bg)] px-3 py-1.5 font-mono text-xs text-[var(--pv-text)]">{value}</code>
                <button data-testid={`demo-copy-${key}`} onClick={() => copy(value, key)} aria-label={`Copy ${label}`} className="rounded-lg border border-[var(--pv-border)] p-1.5 text-[var(--pv-text-3)] hover:text-cyan-400">
                  {copied === key ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button data-testid="demo-regenerate-btn" onClick={regenerate} disabled={busy} className="flex items-center gap-1.5 rounded-full bg-amber-500 px-4 py-2 text-xs font-bold text-amber-950 hover:bg-amber-400 disabled:opacity-60">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          {status?.exists ? "Regenerate credentials" : "Create demo credentials"}
        </button>
        {status?.exists && (
          <>
            <button data-testid="demo-toggle-btn" onClick={toggle} disabled={busy} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-2 text-xs font-bold text-[var(--pv-text-2)] hover:border-amber-500/40 disabled:opacity-60">
              <Power className="h-3.5 w-3.5" /> {status.enabled ? "Disable demo access" : "Enable demo access"}
            </button>
            {!confirmReset ? (
              <button data-testid="demo-reset-btn" onClick={() => setConfirmReset(true)} disabled={busy} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-2 text-xs font-bold text-[var(--pv-text-2)] hover:border-rose-500/40 hover:text-rose-400 disabled:opacity-60">
                <RotateCcw className="h-3.5 w-3.5" /> Reset demo data
              </button>
            ) : (
              <span className="flex items-center gap-2 rounded-full border border-rose-500/40 bg-rose-500/10 px-3 py-1.5">
                <span className="text-[11px] font-bold text-rose-300">Wipe demo world back to baseline?</span>
                <button data-testid="demo-reset-confirm-btn" onClick={reset} disabled={busy} className="text-xs font-bold text-rose-400 hover:text-rose-300">Yes, reset</button>
                <button data-testid="demo-reset-cancel-btn" onClick={() => setConfirmReset(false)} className="text-xs font-semibold text-[var(--pv-text-3)]">Cancel</button>
              </span>
            )}
          </>
        )}
      </div>
      {status?.seeded_at && (
        <p className="mt-3 text-[10px] text-[var(--pv-text-4)]">
          Demo world: {status.demo_counts?.users ?? 0} users · {status.demo_counts?.jobs ?? 0} jobs · {status.demo_counts?.videos ?? 0} videos · {status.demo_counts?.payments ?? 0} payments · seeded {new Date(status.seeded_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
        </p>
      )}
    </div>
  );
};
